import { useCallback, useEffect, useRef } from "react";

/**
 * Scroll choreography for a paginated chat transcript.
 *
 * Two behaviours that pull in opposite directions have to coexist once history
 * is paginated (frontend-doctrine §4.3):
 *
 *  1. NEW messages at the bottom should pin the view to the bottom.
 *  2. OLDER messages prepended at the top must NOT move the view at all.
 *
 * The naive `[messages.length]` dependency cannot tell these apart — a
 * "load earlier" click grows the length just like a new turn does, so it would
 * slam the reader to the newest message every time they tried to read upward,
 * making the affordance actively hostile. So we key the auto-scroll on the
 * IDENTITY OF THE LAST MESSAGE, which is invariant under prepends, and
 * separately compensate for the height a prepend adds.
 */
export function useChatScroll({
  lastMessageId,
  messageCount,
  streamingContent,
  thinkingContent,
  toolEventCount,
  isWaitingForResponse,
}: {
  /** `_id` of the newest rendered message — stable across history prepends. */
  lastMessageId: string | undefined;
  messageCount: number;
  streamingContent: string;
  thinkingContent: string;
  toolEventCount: number;
  isWaitingForResponse: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Height of the scroll content captured immediately before a prepend, so the
  // post-render effect can restore the reader's anchor. `null` = not prepending.
  const prependAnchorRef = useRef<number | null>(null);
  const lastCountRef = useRef(messageCount);

  /**
   * Call immediately BEFORE requesting an older page. Records the current
   * scroll height so the growth can be subtracted back out once React commits
   * the taller list.
   */
  const captureBeforeLoadMore = useCallback(() => {
    prependAnchorRef.current = scrollRef.current?.scrollHeight ?? null;
  }, []);

  // Anchor restoration. Runs layout-synchronously via a ref check rather than
  // useLayoutEffect on purpose: it must observe the SAME commit that grew the
  // list, and it must win before the auto-scroll effect below can act.
  useEffect(() => {
    const el = scrollRef.current;
    const anchor = prependAnchorRef.current;
    const grew = messageCount > lastCountRef.current;
    lastCountRef.current = messageCount;

    if (el && anchor !== null && grew) {
      // Keep the previously-visible first row exactly where the eye left it.
      el.scrollTop += el.scrollHeight - anchor;
      prependAnchorRef.current = null;
    }
  }, [messageCount]);

  // Auto-scroll to the newest content. Deliberately NOT keyed on messageCount —
  // see the module comment. A prepend leaves `lastMessageId` untouched, so this
  // does not fire; a new turn changes it, so it does.
  useEffect(() => {
    if (prependAnchorRef.current !== null) return; // a prepend is in flight
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [
    lastMessageId,
    streamingContent,
    thinkingContent,
    toolEventCount,
    isWaitingForResponse,
  ]);

  return { scrollRef, bottomRef, captureBeforeLoadMore };
}
